import { useInfiniteQuery } from '@tanstack/react-query';
import { TPO_GEN_DIM_OPTIONS_QUERY_KEY } from '../../../utils/tpoGenQueryKeys';
import { tpoGenUrls } from 'config/urls';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { usePlanDetailsContext } from '../../context/PlanDetailsContext';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_LIMIT = 50;

interface SelectOption {
    label: string;
    value: number;
}

const DIM_ATTRIBUTE_TYPE: Record<string, string> = {
    dim1: 'product_category',
    dim2: 'customer_name',
};

const buildDimInclusion = (attrs: string[], dimension: string) => ({
    inclusion: attrs.map((val) => ({
        attributeType: DIM_ATTRIBUTE_TYPE[dimension] ?? dimension,
        attributeDesc: val,
        attributeExId: val,
        dimension,
    })),
    exclusion: [],
});

const fetchEventFilters = async (
    dimension: string,
    attributeType: string,
    filters?: Record<string, unknown>,
    selectedDimension?: SelectedDimensionType,
    signal?: AbortSignal,
    offset?: number,
    searchString?: string,
    planYear?: number,
    lowestAttribute?: boolean,
    dim1Attrs: string[] = [],
    dim2Attrs: string[] = [],
) => {
    const token = localStorage.getItem('accessToken');

    const dimValue = selectedDimension?.value;
    const selectedValues = Array.isArray(dimValue) ? dimValue : dimValue ? [dimValue] : [];

    const selectedDim = (selectedDimension?.dimension as string) || '';
    const selectedInclusion = selectedValues.map((val: string) => ({
        attributeType: selectedDimension?.attributeType,
        attributeDesc: val,
        attributeExId: val,
        dimension: selectedDimension?.dimension,
    }));

    const dim1Base = buildDimInclusion(dim1Attrs, 'dim1');
    const dim2Base = buildDimInclusion(dim2Attrs, 'dim2');

    const filtersObj = {
        dim1:
            selectedDim === 'dim1' && selectedValues.length > 0
                ? { inclusion: [...dim1Base.inclusion, ...selectedInclusion], exclusion: [] }
                : dim1Base,
        dim2:
            selectedDim === 'dim2' && selectedValues.length > 0
                ? { inclusion: [...dim2Base.inclusion, ...selectedInclusion], exclusion: [] }
                : dim2Base,
    };
    const response = await axios(tpoGenUrls.getEventFilters, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: {
            attribute_type: attributeType,
            lowest_attribute: lowestAttribute,
            limit: PAGE_LIMIT,
            offset: offset || 0,
            search_string: searchString ?? '',
            dimension,
            filters: filtersObj || {
                dim1: {},
                dim2: {},
            },
            plan_year: planYear,
        },
        signal: signal,
    });
    return response?.data;
};

interface SelectedDimensionType {
    value: string | string[];
    dimension: string;
    attributeType: string;
}

const useEventAttributeFilters = ({
    dimension,
    attributeType,
    filters,
    lowestAttribute,
    selectedDimension,
    searchString = '',
    enabled = true,
}: {
    dimension: string;
    attributeType: string;
    filters: Record<string, unknown>;
    lowestAttribute: boolean;
    selectedDimension: SelectedDimensionType;
    searchString: string;
    enabled?: boolean;
}) => {
    const [debouncedSearchString, setDebouncedSearchString] = useState(searchString);
    const { currentPlanDetails: planDetails } = usePlanDetailsContext();

    const debouncedSetSearchString = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedSearchString(value);
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedSetSearchString(searchString);
        return () => {
            debouncedSetSearchString.cancel();
        };
    }, [searchString, debouncedSetSearchString]);

    const dim1Attrs = planDetails?.dim1_attrs ?? [];
    const dim2Attrs = planDetails?.dim2_attrs ?? [];

    const { data, isPending, isFetching, isFetchingNextPage, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: [
            TPO_GEN_DIM_OPTIONS_QUERY_KEY,
            dimension,
            attributeType,
            filters,
            selectedDimension,
            debouncedSearchString,
            planDetails?.plan_year,
            lowestAttribute,
            searchString,
            dim1Attrs,
            dim2Attrs,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchEventFilters(
                dimension,
                attributeType,
                filters,
                selectedDimension,
                signal,
                pageParam,
                debouncedSearchString,
                planDetails?.plan_year,
                lowestAttribute,
                dim1Attrs,
                dim2Attrs,
            ),
        enabled,
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return lastPage?.total_count > nextOffset ? nextOffset : undefined;
        },
    });

    const options = useMemo<SelectOption[]>(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => {
            return (page.attribute_values ?? []).map((_attr: { attribute_id: string; attribute_name: string }) => ({
                label: _attr?.attribute_name,
                value: Number(_attr?.attribute_id) || _attr?.attribute_id || _attr?.attribute_name,
            }));
        });
    }, [data]);

    return {
        options,
        isPending,
        isFetching,
        isFetchingNextPage,
        isError,
        hasNextPage,
        fetchNextPage,
    };
};

export default useEventAttributeFilters;
